// Libs
import { Suspense, lazy, ReactNode } from 'react';
import { RouteObject, useParams } from 'react-router';

// Components
import ErrorDisplay from '~fpu/shared/components/ErrorDisplay';
import MemberDashboardLayout from './MemberDashboardLayout';
import PurchasePriceProvider from '~fpu/shared/context/PurchasePriceContext';
import DaveTestimony from './components/DaveTestimony';
import RouteErrorElement from '~fpu/shared/components/RouteErrorElement';
import Budget from './components/Budget/Budget';
import BorrowedFuture from '~fpu/modules/MemberDashboard/components/Courses/BorrowedFuture';

// Lazy-loaded Components
const Achievements = lazy(
  () => import('./components/Achievements/Achievements')
);
const EveryDollarTrialActivation = lazy(
  () => import('./components/EveryDollarTrialActivation')
);
const FavoritePersonality = lazy(
  () => import('./components/MemberProfile/FavoritePersonality')
);
const LessonFeedback = lazy(() => import('./components/LessonFeedback'));
const WorkshopFeedback = lazy(
  () => import('./components/Workshops/WorkshopFeedback')
);
const LessonsPage = lazy(() => import('./LessonsPage/LessonsPage'));
const LessonVideo = lazy(() => import('./components/LessonVideo'));
const MemberDashboard = lazy(() => import('./MemberDashboard'));
const MemberProfile = lazy(
  () => import('./components/MemberProfile/MemberProfile')
);
const Quiz = lazy(() => import('./components/FlashQuiz/Quiz'));
const QuizIntro = lazy(() => import('./components/FlashQuiz/QuizIntro'));
const Resources = lazy(() => import('./components/Resources'));
const UpdateAvatar = lazy(
  () => import('./components/MemberProfile/UpdateAvatar')
);
const Trusted = lazy(() => import('./components/Trusted'));
const Courses = lazy(() => import('./components/Courses/Courses'));
const Course = lazy(() => import('./components/Courses/Course'));
const Clips = lazy(() => import('./components/Clips'));
const Benefits = lazy(() => import('./components/Benefits/Benefits'));
const SmartTax = lazy(() => import('./components/Benefits/SmartTax'));
const MakeYourWill = lazy(() => import('./components/Benefits/MakeYourWill'));
const Audiobooks = lazy(
  () => import('./components/Benefits/Audiobooks/Audiobooks')
);
const SmskCourse = lazy(() => import('./components/SmskCourse'));
const TrackMyWins = lazy(() => import('./components/TrackMyWins/TrackMyWins'));
const BabyStepTracker = lazy(
  () => import('../BabyStepTracker/BabyStepTracker')
);
const ClassDayPoll = lazy(
  () => import('./components/QuizController/ClassDayPoll')
);
const BudgetExercise = lazy(
  () => import('./components/Workshops/BudgetExercise/BudgetExercise')
);
const MoneyPersonalityQuiz = lazy(
  () => import('./components/QuizController/MoneyPersonalityQuiz')
);
const WorkshopMoneyPersonalityQuiz = lazy(
  () => import('./components/QuizController/WorkshopMoneyPersonalityQuiz')
);
const MoneyPersonalityResult = lazy(
  () => import('./components/MemberProfile/MoneyPersonalityResult')
);

const ErrorElement = () => (
  <RouteErrorElement>
    {(status) => (
      <MemberDashboardLayout page="Error">
        <ErrorDisplay code={status} />
      </MemberDashboardLayout>
    )}
  </RouteErrorElement>
);

function QuizLayout({ children }: { children: ReactNode }) {
  const { exerciseId } = useParams();
  return (
    <MemberDashboardLayout page={`Flash Quiz - Lesson ${exerciseId}`}>
      {children}
    </MemberDashboardLayout>
  );
}

const UnauthenticatedErrorElement = () => (
  <RouteErrorElement>
    {(status) => <ErrorDisplay code={status} />}
  </RouteErrorElement>
);

const MEMBER_DASHBOARD_ROUTES: RouteObject[] = [
  {
    path: 'member',
    errorElement: <ErrorElement />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={null}>
            <PurchasePriceProvider>
              <MemberDashboard />
            </PurchasePriceProvider>
          </Suspense>
        )
      },
      {
        path: 'lessons',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={null}>
                <LessonsPage />
              </Suspense>
            )
          },
          {
            path: ':lessonId',
            element: (
              <Suspense fallback={null}>
                <LessonVideo />
              </Suspense>
            )
          },
          {
            path: ':exerciseId',
            children: [
              {
                path: 'feedback',
                element: (
                  <Suspense fallback={null}>
                    <LessonFeedback />
                  </Suspense>
                )
              },
              {
                path: 'quiz-intro',
                element: (
                  <Suspense fallback={null}>
                    <QuizIntro />
                  </Suspense>
                )
              },
              {
                path: 'quiz',
                element: (
                  <Suspense fallback={null}>
                    <QuizLayout>
                      <Quiz />
                    </QuizLayout>
                  </Suspense>
                )
              }
            ]
          },
          {
            path: 'dave-testimony',
            element: (
              <Suspense fallback={null}>
                <DaveTestimony />
              </Suspense>
            )
          }
        ]
      },
      {
        path: 'budget',
        element: (
          <Suspense fallback={null}>
            <MemberDashboardLayout page="Budget">
              <Budget />
            </MemberDashboardLayout>
          </Suspense>
        )
      },
      {
        path: 'resources',
        element: (
          <Suspense fallback={null}>
            <Resources />
          </Suspense>
        )
      },
      {
        path: 'achievements',
        element: (
          <Suspense fallback={null}>
            <Achievements />
          </Suspense>
        )
      },
      {
        path: 'ramsey-trusted',
        element: (
          <Suspense fallback={null}>
            <Trusted />
          </Suspense>
        )
      },
      {
        path: 'profile',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={null}>
                <MemberDashboardLayout page="My Profile">
                  <MemberProfile />
                </MemberDashboardLayout>
              </Suspense>
            )
          },
          {
            path: 'avatar',
            element: (
              <Suspense fallback={null}>
                <UpdateAvatar />
              </Suspense>
            )
          },
          {
            path: 'personality',
            element: (
              <Suspense fallback={null}>
                <FavoritePersonality />
              </Suspense>
            )
          },
          {
            path: 'money-personality',
            element: (
              <Suspense fallback={null}>
                <MoneyPersonalityResult />
              </Suspense>
            )
          }
        ]
      },
      {
        path: 'courses',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={null}>
                <MemberDashboardLayout page="Courses" allAccess>
                  <Courses />
                </MemberDashboardLayout>
              </Suspense>
            )
          },
          {
            path: 'smsk',
            element: (
              <Suspense fallback={null}>
                <MemberDashboardLayout page="SMSK Course" allAccess>
                  <SmskCourse />
                </MemberDashboardLayout>
              </Suspense>
            )
          },
          {
            path: 'borrowed-future',
            element: (
              <Suspense fallback={null}>
                <MemberDashboardLayout page="Borrowed Future" allAccess>
                  <BorrowedFuture />
                </MemberDashboardLayout>
              </Suspense>
            )
          },
          {
            path: ':courseId',
            element: (
              <Suspense fallback={null}>
                <Course />
              </Suspense>
            )
          }
        ]
      },
      {
        path: 'events',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={null}>
                <MemberDashboardLayout page="Events" allAccess>
                  <Courses />
                </MemberDashboardLayout>
              </Suspense>
            )
          },
          {
            path: ':courseId',
            element: (
              <Suspense fallback={null}>
                <Course />
              </Suspense>
            )
          }
        ]
      },
      {
        path: 'benefits',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={null}>
                <MemberDashboardLayout page="Benefits" allAccess>
                  <Benefits />
                </MemberDashboardLayout>
              </Suspense>
            )
          },
          {
            path: 'audiobooks',
            element: (
              <Suspense fallback={null}>
                <MemberDashboardLayout page="Audiobooks" allAccess>
                  <Audiobooks />
                </MemberDashboardLayout>
              </Suspense>
            )
          },
          {
            path: 'make-your-will',
            element: (
              <Suspense fallback={null}>
                <MemberDashboardLayout page="Make Your Will" allAccess>
                  <MakeYourWill />
                </MemberDashboardLayout>
              </Suspense>
            )
          },
          {
            path: 'ramsey-smart-tax',
            element: (
              <Suspense fallback={null}>
                <MemberDashboardLayout page="Smart Tax" allAccess>
                  <SmartTax />
                </MemberDashboardLayout>
              </Suspense>
            )
          }
        ]
      },
      {
        path: 'everydollar-trial',
        element: (
          <Suspense fallback={null}>
            <EveryDollarTrialActivation />
          </Suspense>
        )
      },
      {
        path: 'clips',
        element: (
          <Suspense fallback={null}>
            <MemberDashboardLayout page="Clips">
              <Clips />
            </MemberDashboardLayout>
          </Suspense>
        )
      },
      {
        path: 'track',
        element: (
          <Suspense fallback={null}>
            <MemberDashboardLayout page="Baby Step Tracker">
              <BabyStepTracker />
            </MemberDashboardLayout>
          </Suspense>
        )
      },
      {
        path: 'track-my-wins',
        element: (
          <Suspense fallback={null}>
            <MemberDashboardLayout page="Track My Wins">
              <TrackMyWins />
            </MemberDashboardLayout>
          </Suspense>
        )
      },
      {
        path: 'poll/:classCode/:lessonNumber',
        element: (
          <Suspense fallback={null}>
            <ClassDayPoll />
          </Suspense>
        )
      },
      {
        path: 'workshops/:workshopId/budget-exercise',
        element: (
          <Suspense fallback={null}>
            <MemberDashboardLayout page="Budget Calculator Exercise">
              <BudgetExercise />
            </MemberDashboardLayout>
          </Suspense>
        )
      },
      {
        path: 'workshops/:workshopId/feedback',
        element: (
          <Suspense fallback={null}>
            <WorkshopFeedback />
          </Suspense>
        )
      },
      {
        path: 'workshops/:workshopId/money-personality-quiz',
        element: (
          <Suspense fallback={null}>
            <WorkshopMoneyPersonalityQuiz />
          </Suspense>
        )
      },
      {
        path: 'quiz',
        errorElement: <UnauthenticatedErrorElement />,
        children: [
          {
            path: 'money-personality',
            element: (
              <Suspense fallback={null}>
                <MoneyPersonalityQuiz />
              </Suspense>
            )
          }
        ]
      }
    ]
  }
];

export default MEMBER_DASHBOARD_ROUTES;
